import { useMemo } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import type { CustomSqlSnippet, SqlSnippetEngineId } from '../types/sqlSnippetTypes';
import { useSqlSnippetsStore } from '../store/sqlSnippetsStore';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sqlSnippetFormDefaultValues, sqlSnippetFormSchema, type SqlSnippetFormValues } from './sqlSnippetFormSchema';
type SqlSnippetEditorDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly snippet?: CustomSqlSnippet;
  readonly defaultEngineId: SqlSnippetEngineId;
};
const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) return;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error && 'message' in error && typeof (error as any).message === 'string') {
    return (error as any).message;
  }
  return;
};
export function SqlSnippetEditorDialog({ open, onOpenChange, snippet, defaultEngineId }: SqlSnippetEditorDialogProps) {
  const addSnippet = useSqlSnippetsStore((state) => state.addSnippet);
  const updateSnippet = useSqlSnippetsStore((state) => state.updateSnippet);

  const defaultValues = useMemo<SqlSnippetFormValues>(() => {
    if (snippet) {
      return {
        engineId: snippet.engineId,
        label: snippet.label,
        prefix: snippet.prefix,
        description: snippet.description,
        body: snippet.body,
      };
    }

    return {
      ...sqlSnippetFormDefaultValues,
      engineId: defaultEngineId,
    };
  }, [defaultEngineId, snippet]);

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (snippet) {
        updateSnippet(snippet.id, value);
      } else {
        addSnippet(value);
      }
      onOpenChange(false);
    },
  });
  const canSubmit = useStore(form.store, (state) => state.canSubmit);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px]" data-testid="sql-snippet-editor-dialog">
        <DialogHeader>
          <DialogTitle>{snippet ? 'Edit SQL Snippet' : 'Add SQL Snippet'}</DialogTitle>
        </DialogHeader>

        <form
          id="sql-snippet-form"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <form.Field
              name="engineId"
              validators={{ onChange: sqlSnippetFormSchema.shape.engineId, onSubmit: sqlSnippetFormSchema.shape.engineId }}
            >
              {(field) => (
                <div className="grid gap-1.5">
                  <Label>Engine</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value as SqlSnippetEngineId)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select engine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mssql">SQL Server</SelectItem>
                      <SelectItem value="pgsql">PostgreSQL</SelectItem>
                      <SelectItem value="sqlite">SQLite</SelectItem>
                      <SelectItem value="mysql">MySQL</SelectItem>
                      <SelectItem value="oracle">Oracle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="prefix"
              validators={{ onChange: sqlSnippetFormSchema.shape.prefix, onSubmit: sqlSnippetFormSchema.shape.prefix }}
            >
              {(field) => (
                <div className="grid gap-1.5">
                  <Label>Prefix</Label>
                  <Input
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="e.g. select, update, cte"
                    data-testid="sql-snippet-prefix"
                  />
                  {getErrorMessage(field.state.meta.errors[0]) ? (
                    <p className="text-xs text-destructive">{getErrorMessage(field.state.meta.errors[0])}</p>
                  ) : null}
                </div>
              )}
            </form.Field>
          </div>

          <form.Field
            name="label"
            validators={{ onChange: sqlSnippetFormSchema.shape.label, onSubmit: sqlSnippetFormSchema.shape.label }}
          >
            {(field) => (
              <div className="grid gap-1.5">
                <Label>Label</Label>
                <Input
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="e.g. UPDATE … WHERE …"
                  data-testid="sql-snippet-label"
                />
                {getErrorMessage(field.state.meta.errors[0]) ? (
                  <p className="text-xs text-destructive">{getErrorMessage(field.state.meta.errors[0])}</p>
                ) : null}
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div className="grid gap-1.5">
                <Label>Description</Label>
                <Input
                  value={field.state.value ?? ''}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Optional"
                  data-testid="sql-snippet-description"
                />
              </div>
            )}
          </form.Field>

          <form.Field
            name="body"
            validators={{ onChange: sqlSnippetFormSchema.shape.body, onSubmit: sqlSnippetFormSchema.shape.body }}
          >
            {(field) => (
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>Snippet body</Label>
                  <span className="text-[10px] text-muted-foreground">
                    Monaco placeholders: <span className="font-mono">${'{1:table}'}</span>
                  </span>
                </div>
                <Textarea
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  className="min-h-[180px] font-mono text-xs"
                  placeholder={'SELECT\n  ${1:*}\nFROM ${2:table};'}
                  data-testid="sql-snippet-body"
                />
                {getErrorMessage(field.state.meta.errors[0]) ? (
                  <p className="text-xs text-destructive">{getErrorMessage(field.state.meta.errors[0])}</p>
                ) : null}
              </div>
            )}
          </form.Field>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="sql-snippet-form" disabled={!canSubmit || isSubmitting} data-testid="sql-snippet-save">
            {snippet ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
